<template>
  <main class="px-10">
    <div class="flex items-center justify-end mb-6">
      <TamButton
        @click.prevent="
          actionType = 'add';
          prepareMemberUpdate();
        "
        color="black"
        label="Invite team member"
      />
    </div>
    <div class="relative overflow-x-auto sm:rounded-lg border">
      <table class="w-full text-sm text-left text-gray-500">
        <thead class="text-xs text-gray-700 uppercase bg-light-background">
          <tr>
            <th scope="col" class="px-6 py-3" v-for="item in tableHead" :key="item">{{ item }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in teamMembers?.members" :key="item.uuid" class="bg-white border-b hover:bg-gray-50">
            <td scope="row" class="px-6 py-4 font-medium text-gray-900 dark:text-white whitespace-nowrap">{{ item.name }}</td>

            <td class="px-6 py-4">
              <span class="lowercase">{{ item.email }}</span>
            </td>
            <td class="px-6 py-4 text-brand-black font-bold capitalize">
              {{ item.role }}
            </td>
            <td class="px-6 py-4 flex items-center">
              <button
                v-tooltip.top="'Update'"
                class="w-[20px] h-[20px] cursor-pointer mr-2"
                @click.prevent="
                  actionType = 'update';
                  prepareMemberUpdate(item);
                "
              >
                <img src="@/assets/icons/pencilline.svg" alt="" />
              </button>
              <button
                v-tooltip.top="'Delete'"
                class="cursor-pointer"
                @click.prevent="
                  deleteDetails = item;
                  deleteDialog = true;
                "
              >
                <Trash />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-10" id="invite">
      <div class="mb-6">
        <p class="text-2xl font-normal font-cooper">Invites</p>
        <p class="text-opacity-70 text-brand-black">Invited team members will receive an email. Invites expire after seven (7) days.</p>
      </div>
      <div>
        <div v-if="!hasPendinInvites">
          <Empty title="There are no invites available." text="You can invte team member's by using the invite button above." />
        </div>
        <div class="relative overflow-x-auto sm:rounded-lg border" v-else>
          <table class="w-full text-sm text-left text-gray-500">
            <thead class="text-xs text-gray-700 uppercase bg-light-background">
              <tr>
                <th scope="col" class="px-6 py-3" v-for="item in inviteTableHead" :key="item">{{ item }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in teamMembers?.invites" :key="item.uuid" class="bg-white border-b hover:bg-gray-50">
                <td scope="row" class="px-6 py-4 font-medium text-gray-900 dark:text-white whitespace-nowrap">{{ item.name }}</td>

                <td class="px-6 py-4 lower">
                  <span class="lowercase">{{ item.email }}</span>
                </td>
                <td class="px-6 py-4 text-right text-brand-success">
                  <div class="cursor-pointer" @click="resend(item.email)">Resend</div>
                </td>
                <td class="px-6 py-4 text-right text-brand-danger"><div class="cursor-pointer" @click="revoke(item.uuid)">Revoke invite</div></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </main>

  <TamModal v-show="deleteDialog" size="xl">
    <template #header>
      <div class="flex justify-between items-center">
        <p class="text-xl font-medium">Delete Team Member</p>
        <TamButton @click="deleteDialog = false" :icon="Close" />
      </div>
    </template>
    <template #body>
      <div id="modal-body" class="gap-y-2">
        <div class="mb-4 text-center">
          <p class="text-2xl font-extrabold">Are you sure?</p>
          <p>
            Do you really want to delete <span class="font-FoundersMedium">{{ deleteDetails?.name }}</span
            >? This process cannot be undone.
          </p>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-between gap-4">
        <button type="button" @click.prevent="deleteDialog = false" class="bg-white p-4 py-3 border border-brand-black rounded-lg w-1/2">
          Cancel
        </button>
        <tam-button class="bg-white p-4 py-3 rounded-lg w-1/2" :loading="loading" @click="deleteUser" type="submit" color="info" label="Delete" />
      </div>
    </template>
  </TamModal>

  <add-member-dialog
    :is-active="showDialog"
    :action-type="actionType"
    :details="details"
    :company-roles="enums?.company_roles"
    :roles="enums?.employer_roles"
    @close="closeMemberDialog"
    @confirm="invite"
    @update="updateMember"
  />

  <loading-overlay v-if="loading" />
</template>

<script setup>
import TamTable from '@/components/TamTable.vue';
import TamButton from '@/components/TamButton.vue';
import AddMemberDialog from '@/components/users/AddMember.vue';
import TamModal from '@/components/TamModal.vue';
import Empty from '../components/Empty.vue';
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import More from '@/assets/icons/more.svg?inline';
import { ref, onMounted, computed } from 'vue';
import { Close, Trash } from '@/icons.js';
import { useStore } from 'vuex';
import { errorMessage, alert } from '@/utils/helper';

const store = useStore();

const teamMembers = ref({
  members: [],
  invites: [],
});
const deleteDialog = ref(false);
const deleteDetails = ref(null);
const showDialog = ref(false);
const loading = ref(false);
const actionType = ref('add');
const tableHead = ref(['TEAM MEMBER', 'EMAIL ADDRESS', 'ROLE', 'ACTIONS']);
const inviteTableHead = ref(['FULL NAME', 'EMAIL ADDRESS', '', '']);
const memberToUpdateId = ref(null);
const details = ref({
  first_name: '',
  last_name: '',
  email: '',
  role: '',
  role_at_company: '',
});
const enums = computed(() => store.getters['global/getEnumDetails']);
const hasPendinInvites = computed(() => teamMembers.value.invites?.length > 0);

const closeMemberDialog = () => {
  showDialog.value = false;
  details.value = {
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    role_at_company: '',
  };
};

async function revoke(id) {
  try {
    loading.value = true;
    await store.dispatch('auth/revokeTeamMember', id);
    alert('Revoked', 'success');
    await store.dispatch('auth/getTeamMembers', { forceReload: true });
    teamMembers.value = store.getters['auth/teamMembers'];
  } catch (error) {
    alert(errorMessage(error), 'error');
  } finally {
    loading.value = false;
  }
}

const resend = async (email) => {
  try {
    loading.value = true;
    const { message } = await store.dispatch('auth/resendTeamInvite', { email });
    await store.dispatch('auth/getTeamMembers', { forceReload: true });
    alert(message, 'success');
  } catch (error) {
    if (error.response && error.response.status === 422) {
    }
    alert(errorMessage(error), 'error');
  } finally {
    loading.value = false;
  }
};

const invite = async () => {
  try {
    loading.value = true;
    const { message } = await store.dispatch('auth/inviteTeamMember', details.value);
    alert(message, 'success');
    showDialog.value = false;
    await store.dispatch('auth/getTeamMembers', { forceReload: true });
    teamMembers.value = store.getters['auth/teamMembers'];
  } catch (error) {
    alert(errorMessage(error), 'error');
  } finally {
    loading.value = false;
  }
};

async function deleteUser() {
  try {
    loading.value = true;
    const { message } = await store.dispatch('auth/removeTeamMember', deleteDetails.value.uuid);
    deleteDialog.value = false;
    alert(message, 'success');
    await store.dispatch('auth/getTeamMembers', { forceReload: true });
    teamMembers.value = store.getters['auth/teamMembers'];
  } catch (error) {
    alert(errorMessage(error), 'error');
  } finally {
    loading.value = false;
  }
}
const prepareMemberUpdate = (member) => {
  if (member && actionType.value === 'update') {
    const { uuid, name, ...rest } = member;
    let nameList = name.split(' ');
    let last_name = nameList.slice(1).join(' ');
    memberToUpdateId.value = uuid;
    details.value = { ...rest, first_name: nameList[0], last_name };
  }

  showDialog.value = true;
};

const updateMember = async () => {
  try {
    loading.value = true;
    const { first_name, last_name, ...rest } = details.value;
    const { message } = await store.dispatch('auth/updateTeamMember', {
      id: memberToUpdateId.value,
      details: { ...rest, name: `${first_name} ${last_name}` },
    });

    showDialog.value = false;
    memberToUpdateId.value = null;
    alert(message, 'success');
  } catch (error) {
    alert(errorMessage(error) || 'Something went wrong (Please check internet connection)', 'error', 5000);
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  try {
    if (!Array.isArray(store.getters['global/getEmployerRoles'])) {
      await store.dispatch('global/getEnums');
    }
    await store.dispatch('auth/getTeamMembers', { forceReload: true });
    teamMembers.value = store.getters['auth/teamMembers'];
  } catch (error) {
    alert(errorMessage(error), 'error');
  }
});
</script>
